<template>
  <div 
    class="draggable-widget" 
    :class="{ 'is-dragging': isDragging }"
    :data-widget-id="widgetId"
  >
    <div class="widget-header" @mousedown="startDrag">
      <div class="widget-title-section">
        <i :class="widget.icon" class="widget-icon"></i>
        <h6 class="widget-title">{{ widget.title }}</h6>
      </div>
      <div class="widget-actions">
        <el-dropdown trigger="click" @command="handleAction">
          <i class="ri-more-2-fill"></i>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="refresh">
                <i class="ri-refresh-line me-1"></i> Actualizar
              </el-dropdown-item>
              <el-dropdown-item command="configure">
                <i class="ri-settings-line me-1"></i> Configurar
              </el-dropdown-item>
              <el-dropdown-item command="export">
                <i class="ri-download-line me-1"></i> Exportar
              </el-dropdown-item>
              <el-dropdown-item command="fullscreen">
                <i class="ri-fullscreen-line me-1"></i> Pantalla completa
              </el-dropdown-item>
              <el-dropdown-item command="remove" divided>
                <i class="ri-delete-bin-line me-1"></i> Eliminar
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>
    
    <div class="widget-content" :class="{ 'loading': widget.loading }">
      <div v-if="widget.loading" class="widget-loading">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
      
      <!-- Contenido del widget basado en el tipo -->
      <component 
        v-else
        :is="getWidgetComponent()"
        v-bind="widget.props"
        @widget-action="$emit('widget-action', $event)"
      />
    </div>
    
    <!-- Indicador de redimensionamiento -->
    <div class="resize-handle" @mousedown="startResize"></div>
  </div>
</template>

<script>
import { ref } from 'vue'
import MiniStatisticsCard from '@/examples/Cards/MiniStatisticsCard.vue'
import DoughnutChart from '@/examples/Charts/DoughnutChart.vue'
import ReportsBarChart from '@/examples/Charts/ReportsBarChart.vue'

export default {
  name: 'DraggableWidget',
  components: {
    MiniStatisticsCard,
    DoughnutChart,
    ReportsBarChart
  },
  props: {
    widget: {
      type: Object,
      required: true
    },
    widgetId: {
      type: String,
      required: true
    }
  },
  emits: ['widget-action', 'widget-resize', 'widget-move'],
  setup(props, { emit }) {
    const isDragging = ref(false)
    const isResizing = ref(false)
    
    const getWidgetComponent = () => {
      const componentMap = {
        'stat-card': 'MiniStatisticsCard',
        'doughnut-chart': 'DoughnutChart',
        'bar-chart': 'ReportsBarChart',
        'table': 'el-table',
        'metric': 'MiniStatisticsCard'
      }
      return componentMap[props.widget.type] || 'div'
    }
    
    const startDrag = (event) => {
      isDragging.value = true
      emit('widget-action', {
        type: 'drag-start',
        widgetId: props.widgetId,
        event
      })
    }
    
    const startResize = (event) => {
      event.preventDefault()
      event.stopPropagation()
      isResizing.value = true
      
      const startX = event.clientX
      const startY = event.clientY
      const startWidth = parseInt(document.defaultView.getComputedStyle(event.target.parentElement).width, 10)
      const startHeight = parseInt(document.defaultView.getComputedStyle(event.target.parentElement).height, 10)
      
      const doResize = (e) => {
        const newWidth = startWidth + e.clientX - startX
        const newHeight = startHeight + e.clientY - startY
        
        emit('widget-resize', {
          widgetId: props.widgetId,
          width: newWidth,
          height: newHeight
        })
      }
      
      const stopResize = () => {
        isResizing.value = false
        document.removeEventListener('mousemove', doResize)
        document.removeEventListener('mouseup', stopResize)
      }
      
      document.addEventListener('mousemove', doResize)
      document.addEventListener('mouseup', stopResize)
    }
    
    const handleAction = (command) => {
      emit('widget-action', {
        type: command,
        widgetId: props.widgetId
      })
    }
    
    return {
      isDragging,
      isResizing,
      getWidgetComponent,
      startDrag,
      startResize,
      handleAction
    }
  }
}
</script>

<style scoped>
.draggable-widget {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  transition: all 0.3s ease;
  position: relative;
  min-height: 200px;
  overflow: hidden;
}

.draggable-widget:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: rgba(255, 255, 255, 0.25);
}

.draggable-widget.is-dragging {
  transform: scale(1.02);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  opacity: 0.9;
}

.widget-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px) saturate(150%);
  -webkit-backdrop-filter: blur(10px) saturate(150%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  cursor: grab;
}

.widget-header:active {
  cursor: grabbing;
}

.widget-title-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.widget-icon {
  font-size: 18px;
  color: #4361ee;
}

.widget-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: inherit;
}

.widget-actions i {
  font-size: 16px;
  color: #666;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.widget-actions i:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #4361ee;
}

.widget-content {
  padding: 16px;
  height: calc(100% - 48px);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.widget-content.loading {
  background: rgba(255, 255, 255, 0.02);
}

.widget-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}

.resize-handle {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 20px;
  height: 20px;
  background: linear-gradient(-45deg, transparent 30%, rgba(255,255,255,0.3) 30%, rgba(255,255,255,0.3) 70%, transparent 70%);
  cursor: nw-resize;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.draggable-widget:hover .resize-handle {
  opacity: 1;
}

/* Dark mode styles */
body.dark-mode-active .draggable-widget {
  background: rgba(26, 32, 44, 0.6);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .draggable-widget:hover {
  border-color: rgba(255, 255, 255, 0.2);
}

body.dark-mode-active .widget-header {
  background: rgba(255, 255, 255, 0.05);
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .widget-icon {
  color: #4fc3f7;
}

body.dark-mode-active .widget-title {
  color: rgba(255, 255, 255, 0.9);
}

body.dark-mode-active .widget-actions i {
  color: rgba(255, 255, 255, 0.6);
}

body.dark-mode-active .widget-actions i:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #4fc3f7;
}

body.dark-mode-active .widget-content.loading {
  background: rgba(255, 255, 255, 0.02);
}

body.dark-mode-active .resize-handle {
  background: linear-gradient(-45deg, transparent 30%, rgba(255,255,255,0.2) 30%, rgba(255,255,255,0.2) 70%, transparent 70%);
}
</style>